import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Card, Table, Button, Modal, Form, Row, Col,
  Spinner, Alert, InputGroup, Badge,
} from 'react-bootstrap';
import {
  MagnifyingGlassIcon, PencilSquareIcon,
  ArchiveBoxIcon, PlusCircleIcon, ClipboardDocumentListIcon, ArrowPathIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { useCompanyInventory, getStockStatus } from './hooks/useCompanyInventory';

const thStyle: React.CSSProperties = {
  color: '#475569', fontWeight: 600, fontSize: '0.8rem',
  textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px',
};

function StatusBadge({ status }: { status: 'ok' | 'low' | 'out' }) {
  const map = {
    ok: { bg: '#dcfce7', color: '#166534', label: 'OK' },
    low: { bg: '#fef9c3', color: '#854d0e', label: 'Low' },
    out: { bg: '#fee2e2', color: '#991b1b', label: 'Out' },
  };
  const s = map[status];
  return (
    <Badge bg="" style={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, fontSize: '0.75rem', padding: '3px 8px' }}>
      {s.label}
    </Badge>
  );
}

const CompanyInventory: React.FC = () => {
  const navigate = useNavigate();
  const {
    loading, search, setSearch, statusFilter, setStatusFilter,
    filtered, inventory, feedback, setFeedback,
    adjustModal, setAdjustModal, openAdjust, handleAdjust,
    syncing, syncInventory,
  } = useCompanyInventory();

  if (loading) {
    return (
      <CompanyLayout>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="success" />
          <p className="mt-2 text-muted">Loading inventory...</p>
        </Container>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        {feedback.success && (
          <Alert variant="success" dismissible onClose={() => setFeedback(p => ({ ...p, success: '' }))} className="mb-3">
            {feedback.success}
          </Alert>
        )}
        {feedback.error && (
          <Alert variant="danger" dismissible onClose={() => setFeedback(p => ({ ...p, error: '' }))} className="mb-3">
            {feedback.error}
          </Alert>
        )}

        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-2 mb-2 mb-md-0">
            <div style={{ backgroundColor: '#ecfdf5', borderRadius: '8px', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArchiveBoxIcon style={{ width: '22px', height: '22px', color: '#059669' }} />
            </div>
            <div>
              <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Inventory</h4>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Track stock levels and manage deliveries</div>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" className="d-flex align-items-center gap-1"
              onClick={syncInventory} disabled={syncing} title="Sync stock from received purchase orders"
              style={{ padding: '6px 14px' }}>
              <ArrowPathIcon style={{ width: '15px', height: '15px', animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
              <span className="d-none d-sm-inline">{syncing ? 'Syncing…' : 'Sync Stock'}</span>
            </Button>
            <Button variant="outline-success" size="sm" className="d-flex align-items-center gap-1"
              onClick={() => navigate('/company/purchase-orders/new')} style={{ padding: '6px 14px' }}>
              <ClipboardDocumentListIcon style={{ width: '15px', height: '15px' }} />
              <span className="d-none d-sm-inline">Create Order</span>
            </Button>
            <Button variant="success" size="sm" className="d-flex align-items-center gap-1"
              onClick={() => navigate('/company/purchase-orders?tab=receive')} style={{ padding: '6px 14px' }}>
              <PlusCircleIcon style={{ width: '15px', height: '15px' }} />
              <span className="d-none d-sm-inline">Receive Delivery</span>
            </Button>
          </div>
        </div>

        {/* Stats */}
        <Row className="g-3 mb-4">
          {[
            { label: 'Total Items', value: inventory.length, color: '#059669', bg: '#ecfdf5' },
            { label: 'Low Stock', value: inventory.filter(r => getStockStatus(r) === 'low').length, color: '#d97706', bg: '#fef3c7' },
            { label: 'Out of Stock', value: inventory.filter(r => getStockStatus(r) === 'out').length, color: '#dc2626', bg: '#fee2e2' },
          ].map(stat => (
            <Col key={stat.label} xs={4} md={3} lg={2}>
              <Card className="border-0 shadow-sm text-center" style={{ borderRadius: '10px' }}>
                <Card.Body className="py-3">
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{stat.label}</div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '10px' }}>
          <Card.Body className="py-3">
            <Row className="g-2 align-items-center">
              <Col xs={12} md={6}>
                <InputGroup size="sm">
                  <InputGroup.Text style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px 0 0 6px' }}>
                    <MagnifyingGlassIcon style={{ width: '15px', height: '15px', color: '#94a3b8' }} />
                  </InputGroup.Text>
                  <Form.Control placeholder="Search ingredients..." value={search} onChange={e => setSearch(e.target.value)}
                    style={{ border: '1px solid #e2e8f0', borderRadius: '0 6px 6px 0' }} />
                </InputGroup>
              </Col>
              <Col xs={12} md={3}>
                <Form.Select size="sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                  <option value="all">All statuses</option>
                  <option value="low">Low / Out of stock</option>
                </Form.Select>
              </Col>
              <Col className="text-muted" style={{ fontSize: '0.85rem' }}>
                {filtered.length} of {inventory.length} items
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Table */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
          {inventory.length === 0 ? (
            <Card.Body className="text-center py-5 text-muted">
              <ArchiveBoxIcon style={{ width: '36px', height: '36px', opacity: 0.3, marginBottom: '8px' }} />
              <div className="fw-semibold mb-1">No inventory data yet</div>
              <div style={{ fontSize: '0.85rem' }}>Receive a delivery to start tracking stock.</div>
              <Button variant="success" size="sm" className="mt-3"
                onClick={() => navigate('/company/purchase-orders/new')}>
                Create First Order
              </Button>
            </Card.Body>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={thStyle}>Ingredient</th>
                    <th style={thStyle} className="d-none d-md-table-cell">Category</th>
                    <th style={thStyle}>Stock</th>
                    <th style={thStyle} className="d-none d-md-table-cell">Reorder Point</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle} className="d-none d-lg-table-cell">Last Updated</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-4 text-muted">No items match your filters.</td></tr>
                  ) : (
                    filtered.map(r => (
                      <tr key={r.id}>
                        <td style={{ padding: '12px 16px', fontWeight: 500, color: '#1e293b' }}>{r.ingredient_name}</td>
                        <td style={{ padding: '12px 16px' }} className="d-none d-md-table-cell">
                          <Badge bg="" style={{ backgroundColor: '#f1f5f9', color: '#475569', fontWeight: 500 }}>{r.ingredient_category_display}</Badge>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#1e293b', fontWeight: 600 }}>
                          {parseFloat(r.current_stock).toLocaleString()} {r.ingredient_unit}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-md-table-cell">
                          {r.reorder_point ? `${parseFloat(r.reorder_point).toLocaleString()} ${r.ingredient_unit}` : '—'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <StatusBadge status={getStockStatus(r)} />
                        </td>
                        <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '0.8rem' }} className="d-none d-lg-table-cell">
                          {new Date(r.updated_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <Button variant="outline-secondary" size="sm" onClick={() => openAdjust(r)} style={{ padding: '4px 8px' }} title="Adjust stock">
                            <PencilSquareIcon style={{ width: '14px', height: '14px' }} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card>
      </Container>

      {/* Adjust Stock Modal */}
      <Modal show={adjustModal.show} onHide={() => setAdjustModal(p => ({ ...p, show: false }))} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>
            Adjust Stock — {adjustModal.record?.ingredient_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              Current Stock ({adjustModal.record?.ingredient_unit})
            </Form.Label>
            <Form.Control size="sm" type="number" min="0" step="0.001"
              value={adjustModal.newStock}
              onChange={e => setAdjustModal(p => ({ ...p, newStock: e.target.value }))} />
          </Form.Group>
          <Form.Group>
            <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              Reorder Point ({adjustModal.record?.ingredient_unit}) <span className="text-muted fw-normal">optional</span>
            </Form.Label>
            <Form.Control size="sm" type="number" min="0" step="0.001"
              value={adjustModal.newReorder}
              onChange={e => setAdjustModal(p => ({ ...p, newReorder: e.target.value }))}
              placeholder="—" />
            <Form.Text className="text-muted">Alert when stock drops to or below this level</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setAdjustModal(p => ({ ...p, show: false }))} disabled={adjustModal.saving}>Cancel</Button>
          <Button variant="success" size="sm" onClick={handleAdjust} disabled={adjustModal.saving}>
            {adjustModal.saving ? <><Spinner size="sm" animation="border" className="me-1" />Saving...</> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </CompanyLayout>
  );
};

export default CompanyInventory;
